import React, { Component } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import moment from 'moment';

import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import { timestampToDate, monthIdString, dateFromAPIToLocalNoon } from '../../../util/dates';
import { propTypes, isJetSkiListingType, isWatersportListingType } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';
import { HOUR, UNIT } from '../../../constants/availabilityTypes';

import { Form, H6, PrimaryButton, NamedLink, FieldTextInput } from '../../../components';

import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import FieldDateAndTimeInput from './FieldDateAndTimeInput';

import {
  required,
  composeValidators,
  onlyNumbers,
  positiveNumberValues,
  numberFormatValid,
  maxNumberValues,
  minNumberValues
} from '../../../util/validators';

import css from './BookingTimeForm.module.css';

export class BookingTimeFormComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedDuration: null,
      error: null,
    };

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
    this.onChangeDuration = this.onChangeDuration.bind(this);
  }

  onChangeDuration(values) {
    this.setState({ selectedDuration: values });
  }

  handleFormSubmit(e) {
    const { onSubmit, listingCategory } = this.props;
    const isJetSkiListing = isJetSkiListingType(listingCategory);
    const isWatersportListing = isWatersportListingType(listingCategory);

    onSubmit({ ...e, type: (isJetSkiListing || isWatersportListing ) ? UNIT : HOUR });
  }

  // When the values of the form are updated we need to fetch
  // lineItems from this template's backend for the EstimatedTransactionMaybe
  // In case you add more fields to the form, make sure you add
  // the values here to the orderData object.
  handleOnChange(formValues) {
    const { selectedDuration: duration } = this.state;
    // const { bookingStartTime=null, bookingEndTime=null, seats } = formValues?.values;

    const bookingStartTime = formValues?.values?.bookingStartTime ||  null;
    const bookingEndTime = formValues?.values?.bookingEndTime || null;
    const seats = formValues?.values?.seats;

    const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : {};
    const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : {};

    const listingId = this.props.listingId;
    const isOwnListing = this.props.isOwnListing;

    // We expect values bookingStartTime and bookingEndTime to be strings
    // which is the default case when the value has been selected through the form
    // const isSameTime = bookingStartTime === bookingEndTime;

    // const isSameDay = !!moment(+bookingStartTime).isSame(moment(+bookingEndTime), 'day');
    if (bookingStartTime === 'start-time-value') {
      return this.setState({ error: 'Please select time' });
    }

    if (bookingStartTime && bookingEndTime && (duration || formValues?.duration)) {
      const { onFetchTransactionLineItems, listingCategory } = this.props;
      const isJetSkiListing = isJetSkiListingType(listingCategory);
      const isWatersportListing = isWatersportListingType(listingCategory);

      onFetchTransactionLineItems({
        orderData: {
          bookingStart: startDate,
          bookingEnd: endDate,
          type: (isJetSkiListing || isWatersportListing) ? UNIT : HOUR,
          seats,
        },
        listingId,
        isOwnListing,
        listingCategory,
      });
      this.setState({ error: null });
    }
  }

  render() {
    const {
      rootClassName,
      className,
      price: unitPrice,
      dayCountAvailableForBooking,
      marketplaceName,
      ...rest
    } = this.props;
    const classes = classNames(rootClassName || css.root, className);

    return (
      <FinalForm
        {...rest}
        unitPrice={unitPrice}
        onSubmit={this.handleFormSubmit}
        render={fieldRenderProps => {
          const {
            endDatePlaceholder,
            startDatePlaceholder,
            form,
            pristine,
            handleSubmit,
            intl,
            isOwnListing,
            listingId,
            values,
            monthlyTimeSlots,
            onFetchTimeSlots,
            timeZone,
            lineItems,
            fetchLineItemsInProgress,
            fetchLineItemsError,
            isVerifiedIdentify,
            isInstantBooking,
            listingCategory,
            boatPassengerCapacity,
            invalid
          } = fieldRenderProps;

          const onChangeCapacity = (e) => {
            const value = e.target.value.replace(/[^0-9]/g,"")
            form.change("passengerCapacity", Number(value).toString())
          }

          const startTime = values && values?.bookingStartTime ? values?.bookingStartTime : null;
          const endTime = values && values.bookingEndTime ? values.bookingEndTime : null;
          const startDate = startTime ? timestampToDate(startTime) : null;
          const endDate = endTime ? timestampToDate(endTime) : null;

          // This is the place to collect breakdown estimation data. See the
          // EstimatedCustomerBreakdownMaybe component to change the calculations
          // for customized payment processes.
          const breakdownData =
            startDate && endDate
              ? {
                  startDate,
                  endDate,
                }
              : null;

          const showEstimatedBreakdown =
            breakdownData &&
            lineItems &&
            !fetchLineItemsInProgress &&
            !fetchLineItemsError &&
            this.state.selectedDuration &&
            !this.state.error;

          const isJetSkiListing = isJetSkiListingType(listingCategory);
          const isWatersportListing = isWatersportListingType(listingCategory);

          const requiredMessage = intl.formatMessage({ id: 'CustomExtendedDataField.required' })
          const validNumberMessage = intl.formatMessage({ id: 'CustomExtendedDataField.validNumber' })
          const validMaxLimitMessage = intl.formatMessage({ id: 'CustomExtendedDataField.maxLimit' }, { maxLimit: +boatPassengerCapacity || 200 })
          const validMinLimitMessage = intl.formatMessage({ id: 'CustomExtendedDataField.minLimit' }, { minLimit: 1 })

          return (
            <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
              <FormSpy
                subscription={{ values: true }}
                onChange={values => {
                  if (+values?.values?.bookingStartTime !== +values?.values?.bookingEndTime) {
                    return this.handleOnChange(values);
                  }
                }}
              />

              {monthlyTimeSlots && timeZone && !!boatPassengerCapacity ? (
                <FieldTextInput
                  className={css.textField}
                  type="number"
                  id="passengerCapacity"
                  name="passengerCapacity"
                  min={1}
                  step={1}
                  label={intl.formatMessage({
                    id: 'BookingTimeForm.passengerCapacityLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'BookingTimeForm.passengerCapacityPlaceholder',
                  })}
                  onChange={e => onChangeCapacity(e)}
                  validate={
                    composeValidators(
                      required(requiredMessage),
                      onlyNumbers(validNumberMessage),
                      positiveNumberValues(validNumberMessage),
                      numberFormatValid(validNumberMessage),
                      maxNumberValues(validMaxLimitMessage, +boatPassengerCapacity),
                      minNumberValues(validMinLimitMessage, 1),
                    )
                  }
                />
              ) : null}
              
              {monthlyTimeSlots && timeZone ? (
                <FieldDateAndTimeInput
                  startDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingTimeForm.bookingStartTitle' }),
                    placeholderText: startDatePlaceholder,
                  }}
                  endDateInputProps={{
                    label: intl.formatMessage({ id: 'BookingTimeForm.bookingEndTitle' }),
                    placeholderText: endDatePlaceholder,
                  }}
                  className={css.bookingDates}
                  listingId={listingId}
                  onFetchTimeSlots={onFetchTimeSlots}
                  monthlyTimeSlots={monthlyTimeSlots}
                  values={values}
                  intl={intl}
                  form={form}
                  pristine={pristine}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                  selectedDuration={this.state.selectedDuration}
                  onChangeDuration={this.onChangeDuration}
                  duration={this.props.duration}
                  handleOnChange={this.handleOnChange}
                  isJetSkiListing={isJetSkiListing}
                  isWatersportListing={isWatersportListing}
                />
              ) : null}

              {showEstimatedBreakdown ? (
                <div className={css.priceBreakdownContainer}>
                  <H6 as="h3" className={css.bookingBreakdownTitle}>
                    <FormattedMessage id="BookingTimeForm.priceBreakdownTitle" />
                  </H6>
                  <hr className={css.totalDivider} />
                  <EstimatedCustomerBreakdownMaybe
                    breakdownData={breakdownData}
                    lineItems={lineItems}
                    timeZone={timeZone}
                    currency={unitPrice.currency}
                    marketplaceName={marketplaceName}
                    processName={BOOKING_PROCESS_NAME}
                    passengerCapacity={values?.passengerCapacity || null}
                  />
                </div>
              ) : null}

              {fetchLineItemsError ? (
                <span className={css.sideBarError}>
                  <FormattedMessage id="BookingTimeForm.fetchLineItemsError" />
                </span>
              ) : null}

              {!!this.state.error && <span className={css.customError}>{this.state.error}</span>}

              {!isVerifiedIdentify && !isOwnListing ? (
                <p className={classNames(css.finePrint, css.warning)}>
                  <FormattedMessage id="BookingTimeForm.needIdVerification" />
                  <NamedLink name="IdVerificationPage" className={css.logoLink}>
                    <FormattedMessage id="BookingTimeForm.verifyNow" />
                  </NamedLink>
                </p>
              ) : (
                <p className={css.finePrint}>
                  <FormattedMessage
                    id={
                      isOwnListing
                        ? 'BookingTimeForm.ownListing'
                        : 'BookingTimeForm.youWontBeChargedInfo'
                    }
                  />
                </p>
              )}

              <div className={css.submitButton}>
                <PrimaryButton
                  type="submit"
                  inProgress={fetchLineItemsInProgress}
                  disabled={
                    !isVerifiedIdentify ||
                    !this.state.selectedDuration ||
                    this.state.error ||
                    !values?.bookingStartTime || 
                    (boatPassengerCapacity && invalid)
                  }
                >
                  {isInstantBooking ? (
                    <FormattedMessage id="BookingTimeForm.instantBooking" />
                  ) : (
                    <FormattedMessage id="BookingTimeForm.requestToBook" />
                  )}
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

BookingTimeFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  listingId: null,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

BookingTimeFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  timeZone: string.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,

  dayCountAvailableForBooking: number.isRequired,
};

const BookingTimeForm = compose(injectIntl)(BookingTimeFormComponent);
BookingTimeForm.displayName = 'BookingTimeForm';

export default BookingTimeForm;
